import React from 'react';
import { IconType } from 'react-icons';
import ChakraIcon from '../components/common/ChakraIcon';

import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Image,
  Stack,
  Icon,
  Divider,
  VStack,
  HStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaHandshake, 
  FaLeaf, 
  FaBalanceScale, 
  FaUsers,
  FaHeart,
  FaDove,
  FaLightbulb,
  FaSmile
} from 'react-icons/fa';

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const About: React.FC = () => {
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };
  
  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Define interfaces for data
  interface CoreValue {
    title: string;
    description: string;
    icon: IconType;
    color: string;
  }

  interface Principle {
    title: string;
    description: string;
    icon: IconType;
  }

  // Core values data
  const coreValues: CoreValue[] = [
    {
      title: 'Respect for Life',
      description: 'We uphold the inherent dignity and worth of every human life.',
      icon: FaHeart,
      color: 'red.500'
    },
    {
      title: 'Commitment to Peace',
      description: 'We actively work towards peaceful resolution of conflicts.',
      icon: FaDove,
      color: 'blue.500'
    },
    {
      title: 'Cultivation of Hope',
      description: 'We inspire optimism and positive vision for the future.',
      icon: FaLightbulb,
      color: 'yellow.500'
    },
    {
      title: 'Promotion of Happiness',
      description: 'We foster holistic well-being in individuals and communities.',
      icon: FaSmile,
      color: 'orange.500'
    },
    {
      title: 'Collaboration',
      description: 'We work together with diverse stakeholders to achieve common goals.',
      icon: FaHandshake,
      color: 'purple.500'
    },
    {
      title: 'Equity and Justice',
      description: 'We advocate for fair treatment and equal opportunities for all.',
      icon: FaBalanceScale,
      color: 'teal.500'
    },
    {
      title: 'Empowerment',
      description: 'We build capacity and enable communities to lead their own development.',
      icon: FaUsers,
      color: 'green.500'
    }
  ];

  // Principles data
  const principles: Principle[] = [
    {
      title: 'Collaboration',
      description: 'Working together with communities, organizations, and stakeholders to achieve shared goals.',
      icon: FaHandshake,
    },
    {
      title: 'Sustainability',
      description: 'Ensuring long-term impact through environmentally, socially, and economically viable approaches.',
      icon: FaLeaf,
    },
    {
      title: 'Equity',
      description: 'Promoting fair and impartial access to resources and opportunities for all individuals.',
      icon: FaBalanceScale,
    },
    {
      title: 'Empowerment',
      description: 'Building capacity and enabling communities to lead their own development processes.',
      icon: FaUsers,
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        position="relative"
        h={{ base: '40vh', md: '50vh' }}
        bgImage="linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/assets/images/photo_2025-05-04_15-29-39.jpg')"
        bgPosition="center"
        bgSize="cover"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="container.xl" textAlign="center">
          <MotionHeading
            as={motion.h1}
            fontSize={{ base: '3xl', md: '5xl' }}
            fontWeight="bold"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            About ALPHA
          </MotionHeading>
        </Container>
      </Box>

      {/* Introduction Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionFlex
            direction={{ base: 'column', lg: 'row' }}
            align="center"
            justify="space-between"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerChildren}
          >
            <MotionBox
              flex="1"
              mr={{ base: 0, lg: 10 }}
              mb={{ base: 10, lg: 0 }}
              variants={slideUp}
            >
              <Image
                src="/assets/images/photo_2025-05-04_15-29-45.jpg"
                alt="ALPHA Team"
                borderRadius="lg"
                shadow="xl"
                w="full"
                h={{ base: '300px', md: '400px' }}
                objectFit="cover"
              />
            </MotionBox>
            
            <MotionBox flex="1" variants={slideUp}>
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
                color="brand.500"
              >
                Who We Are
              </Heading>
              
              <Divider w="100px" borderColor="brand.500" borderWidth="2px" mb={6} />
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={6}>
                ACTION FOR LIFE PEACE AND HOPE ADVANCEMENT (ALPHA) is a collaborative, faith-based, non-political, non-religious, and non-racial non-profit organization currently operating in Kasindi, Basongora groupement, Beni territory, North Kivu Province, Democratic Republic of Congo (DRC).
              </Text>
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={6}>
                Founded with a vision to foster a world where all individuals and communities thrive, ALPHA works through education, dialogue, and advocacy to empower vulnerable and marginalized communities, including youth and those affected by conflict, poverty, and inequality.
              </Text>
              
              <Text fontSize={{ base: 'md', md: 'lg' }}>
                Our approach is collaborative and community-centered, emphasizing local ownership, transparency, and sustainable development to create lasting positive change.
              </Text>
            </MotionBox>
          </MotionFlex>
        </Container>
      </Box>

      {/* Vision & Mission Section */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16}>
            <Box order={{ base: 2, md: 1 }}>
              <MotionBox
                textAlign="center"
                mb={16}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
              >
                <Heading
                  as="h2"
                  fontSize={{ base: '2xl', md: '3xl' }}
                  mb={4}
                >
                  Our Vision & Mission
                </Heading>
                
                <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
              </MotionBox>
            </Box>
            <Box>
              <Image
                src="/assets/images/photo_2025-05-04_15-30-38.jpg"
                alt="ALPHA Vision"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </Box>
            <MotionFlex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align="stretch"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerChildren}
            >
              <MotionBox
                flex="1"
                bg="white"
                p={8}
                borderRadius="lg"
                boxShadow="md"
                mr={{ base: 0, md: 6 }}
                mb={{ base: 8, md: 0 }}
                variants={slideUp}
              >
                <VStack align="flex-start" spacing={4}>
                  <Heading as="h3" fontSize="2xl" color="brand.500">
                    Our Vision
                  </Heading>
                  
                  <Text fontSize="lg">
                    A world where all individuals and communities experience safety, peace, and happiness, with equitable access to resources and opportunities for a fulfilling life.
                  </Text>
                </VStack>
              </MotionBox>
              
              <MotionBox
                flex="1"
                bg="white"
                p={8}
                borderRadius="lg"
                boxShadow="md"
                variants={slideUp}
              >
                <VStack align="flex-start" spacing={4}>
                  <Heading as="h3" fontSize="2xl" color="brand.500">
                    Our Mission
                  </Heading>
                  
                  <Text fontSize="lg">
                    To empower individuals and communities to thrive by promoting the preservation of life, fostering sustainable peace, cultivating hope, and advancing holistic well-being through collaborative initiatives, education, and advocacy.
                  </Text>
                </VStack>
              </MotionBox>
            </MotionFlex>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Background Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
            <Box>
              <Image
                src="/assets/images/photo_2025-05-04_15-30-38.jpg"
                alt="ALPHA Background"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </Box>
            <MotionBox
              textAlign="center"
              mb={16}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Our Background
              </Heading>
              
              <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            </MotionBox>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Principles Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Our Guiding Principles
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              These principles form the foundation of our approach and guide our work in every community we serve.
            </Text>
          </MotionBox>
          
          <MotionFlex
            as={motion.div}
            wrap="wrap"
            justify="center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={10} w="full">
              {principles.map((principle, index) => (
                <MotionBox
                  key={index}
                  variants={slideUp}
                  textAlign="center"
                >
                  <VStack spacing={4}>
                    <Flex
                      w="80px"
                      h="80px"
                      borderRadius="full"
                      bg="brand.50"
                      color="brand.500"
                      justify="center"
                      align="center"
                    >
                      <ChakraIcon icon={principle.icon} boxSize="40px" />
                    </Flex>
                    
                    <Heading as="h3" fontSize="xl">
                      {principle.title}
                    </Heading>
                    
                    <Text color="gray.600">
                      {principle.description}
                    </Text>
                  </VStack>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionFlex>
        </Container>
      </Box>

      {/* Core Values Section */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Our Core Values
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              These values define who we are and how we work to achieve our mission.
            </Text>
          </MotionBox>
          
          <MotionFlex
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerChildren}
          >
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={8} w="full">
              {coreValues.map((value, index) => (
                <MotionBox
                  key={index}
                  p={5}
                  borderRadius="lg"
                  boxShadow="md"
                  bg="white"
                  transition={{ duration: 0.3, ease: "ease" }}
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
                  variants={slideUp}
                >
                  <HStack spacing={4} align="flex-start">
                    <Flex
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg={`${value.color}10`}
                      color={value.color}
                      justify="center"
                      align="center"
                      mt={1}
                    >
                      <ChakraIcon icon={value.icon} boxSize="20px" />
                    </Flex>
                    
                    <VStack align="flex-start" spacing={2}>
                      <Heading as="h3" fontSize="lg">
                        {value.title}
                      </Heading>
                      
                      <Text color="gray.600" fontSize="sm">
                        {value.description}
                      </Text>
                    </VStack>
                  </HStack>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionFlex>
        </Container>
      </Box>

      {/* Team Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Our Approach
            </Heading>
            
            <Divider w="100px" borderColor="brand.500" borderWidth="2px" mx="auto" mb={6} />
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              Our operational style is collaborative and community-centered, emphasizing:
            </Text>
          </MotionBox>
          
          <MotionFlex
            direction={{ base: 'column', lg: 'row' }}
            align="center"
            justify="space-between"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <MotionBox
              flex="1"
              mr={{ base: 0, lg: 10 }}
              mb={{ base: 10, lg: 0 }}
              variants={slideUp}
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <Box p={5} bg="brand.50" borderRadius="lg">
                  <Heading as="h3" fontSize="lg" mb={2} color="brand.500">
                    Local Ownership
                  </Heading>
                  <Text>Ensuring communities drive their own development processes and solutions.</Text>
                </Box>
                
                <Box p={5} bg="green.50" borderRadius="lg">
                  <Heading as="h3" fontSize="lg" mb={2} color="green.500">
                    Transparency
                  </Heading>
                  <Text>Operating with openness and accountability in all our activities.</Text>
                </Box>
                
                <Box p={5} bg="earth.50" borderRadius="lg">
                  <Heading as="h3" fontSize="lg" mb={2} color="earth.500">
                    Sustainable Development
                  </Heading>
                  <Text>Creating lasting change that continues beyond our direct involvement.</Text>
                </Box>
                
                <Box p={5} bg="blue.50" borderRadius="lg">
                  <Heading as="h3" fontSize="lg" mb={2} color="blue.500">
                    Inclusivity
                  </Heading>
                  <Text>Ensuring all voices are heard and represented in our work.</Text>
                </Box>
              </SimpleGrid>
            </MotionBox>
            
            <MotionBox
              flex="1"
              variants={slideUp}
            >
              <Image
                src="/assets/images/photo_2025-05-04_15-29-50.jpg"
                alt="ALPHA Approach"
                borderRadius="lg"
                shadow="xl"
                w="full"
                h={{ base: '300px', md: '400px' }}
                objectFit="cover"
              />
            </MotionBox>
          </MotionFlex>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
